import type { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useWindowEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { ReferralContext } from "@redotech/redo-customer-portal-shared/contexts/referral";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  CompensationMethod,
  CompensationMethodType,
  DraftReturn,
  ShipmentMethod,
} from "@redotech/redo-model/draft-return/draft-return";
import { ReturnProduct } from "@redotech/redo-model/draft-return/draft-return-items";
import { friendlyCreateDraftReturnErrorMessages } from "@redotech/redo-model/draft-return/errors/create-draft-return";
import { friendlyErrorMessage } from "@redotech/redo-model/draft-return/errors/friendly-error-message";
import { CollectionProduct } from "@redotech/redo-model/non-shopify-claims";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import {
  InputAnswerPart,
  InputPartType,
} from "@redotech/redo-model/return-flow";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Modal } from "@redotech/redo-web/modal";
import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { RpcClientErrorSubscribersContext } from "../contexts/RpcClientContext";
import { ProductSelect } from "../register/product-select";
import { User } from "../user";
import { ChooseItems } from "./choose-items";
import {
  availableMethodsForCompensationMethodsModal,
  CompensationMethodModal,
} from "./compensation-method";
import { ExchangeContext, NewItems } from "./new-items";
import { RegistrationReview } from "./registration-review";
import * as returnStyles from "./return.module.css";
import { Review } from "./review";
import { ContactRedoModal } from "./review-components/contact-redo-modal";
import { ShipmentMethodsModal } from "./shipment-methods/shipment-methods-modal";

export enum ReturnStep {
  ProductSelect = 0,
  Items = 1,
  CompensationMethod = 2,
  NewItems = 3,
  ShipmentMethod = 4,
  Review = 5,
  RegistrationReview = 6,
}

type DraftReturnIDParams =
  | { orderId: string; returnType: string }
  | { productIds: string[]; warrantyId?: string };

const getDraftReturnKey = (params: DraftReturnIDParams) => {
  if ("orderId" in params) {
    return `draftReturnId-${params.orderId}-${params.returnType}`;
  }
  return `draftReturnId-${params.warrantyId ? params.warrantyId : params.productIds.join("-")}`;
};

const getDraftReturnID = (params: DraftReturnIDParams) => {
  if (window.location.search.includes("draftReturnId")) {
    return new URLSearchParams(window.location.search).get("draftReturnId");
  }
  return localStorage.getItem(getDraftReturnKey(params));
};

const setDraftReturnID = (
  params: DraftReturnIDParams,
  draftReturnId: string,
) => {
  localStorage.setItem(getDraftReturnKey(params), draftReturnId);
};

const clearDraftReturnID = (params: DraftReturnIDParams) => {
  localStorage.removeItem(getDraftReturnKey(params));
};

const getReturnType = (
  storeUrl: string | undefined,
  returnTypeParam: string | undefined,
  isClaim: boolean,
  isRegister: boolean,
  isWarranty: boolean,
  isThirdPartyClaim: boolean,
) => {
  // Summer Fridays recycling program
  if (isThirdPartyClaim && storeUrl === "summerfridaysbeauty.myshopify.com") {
    return ReturnTypeEnum.WARRANTY;
  }

  if (returnTypeParam) {
    return returnTypeParam as ReturnTypeEnum;
  }
  if (isClaim || isThirdPartyClaim) {
    return ReturnTypeEnum.CLAIM;
  }
  if (isRegister || isWarranty) {
    return ReturnTypeEnum.WARRANTY;
  }
  return ReturnTypeEnum.RETURN;
};

const DRAFT_RETURN_TIMEOUT = 60 * 29 * 1000;

export function CreateReturn({
  settings,
  user,
  nonShopifyClaimsSelectedProducts,
  setBackHandler,
  onBack,
  passedDraftReturnId,
  callback,
}: {
  settings: ReturnAppSettings;
  user?: User;
  // Used for non-shopify claims
  nonShopifyClaimsSelectedProducts?: CollectionProduct[];
  setBackHandler: React.Dispatch<
    React.SetStateAction<(() => boolean) | undefined>
  >;
  onBack?: () => void;
  passedDraftReturnId?: string;
  callback?: string;
}) {
  const params = useParams<{
    id: string;
    returnType: string;
    warrantyId: string;
  }>();
  const referralContext = useContext(ReferralContext);
  const notes: InputAnswerPart[] = [];
  const isClaim = window.location.pathname.includes("/claim");
  const isRegister = window.location.pathname.includes("/register");
  const isWarranty = window.location.pathname.includes("/warranty");
  const isThirdPartyClaim =
    window.location.pathname.includes("/warranties") &&
    !isRegister &&
    !isWarranty;

  const returnType = getReturnType(
    settings.storeUrl,
    params.returnType,
    isClaim,
    isRegister,
    isWarranty,
    isThirdPartyClaim,
  );
  const orderId = params.id;
  const warrantyId = params.warrantyId;

  const productIds = nonShopifyClaimsSelectedProducts?.flatMap((product) =>
    Array(product.quantity || 1).fill(product.id),
  );

  if (nonShopifyClaimsSelectedProducts?.some((product) => product.bundle)) {
    notes.push({
      message: "Bundled products:",
      type: InputPartType.TEXTAREA,
      value:
        nonShopifyClaimsSelectedProducts?.reduce((acc, product) => {
          product.bundle?.forEach((bundleProduct) => {
            acc += `${bundleProduct.title} x ${bundleProduct.quantity}\n`;
          });
          return acc;
        }, "") ?? "",
    });
  }

  const backHandler = useHandler(() => {
    switch (step) {
      case ReturnStep.ProductSelect:
      case ReturnStep.Items:
        if (onBack) onBack();
        return false;
      case ReturnStep.CompensationMethod:
      case ReturnStep.NewItems:
      case ReturnStep.ShipmentMethod:
      case ReturnStep.RegistrationReview:
      case ReturnStep.Review:
        setStep(ReturnStep.Items);
        return true;
    }
  });

  useEffect(() => {
    setBackHandler(() => backHandler);

    return () => {
      setBackHandler(undefined);
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  const [step, setStep] = useState(
    isRegister ? ReturnStep.ProductSelect : ReturnStep.Items,
  );
  const [draftReturn, setDraftReturn] = useState<DraftReturn | null>(null);
  const [expired, setExpired] = useState<boolean>(false);

  const draftReturnIDParams: DraftReturnIDParams = productIds
    ? { productIds, warrantyId }
    : { orderId: orderId!, returnType: returnType! };

  //If passedDraftreturnId exists it means we came from shop mini, go straight to review
  useEffect(() => {
    if (passedDraftReturnId && step !== ReturnStep.Review) {
      setStep(ReturnStep.Review);
    }
  }, [step, passedDraftReturnId]);

  // Draft return will be deleted after 30 minutes,
  // Tell the customer that the session has expired when that happens.
  useEffect(() => {
    if (!draftReturn) {
      return;
    }
    const timeoutID = setTimeout(() => {
      setExpired(true);
      const draftReturnIDParams: DraftReturnIDParams = productIds
        ? { productIds, warrantyId }
        : { orderId: orderId!, returnType: returnType! };
      clearDraftReturnID(draftReturnIDParams);
    }, DRAFT_RETURN_TIMEOUT);
    return () => {
      clearTimeout(timeoutID);
    };
  }, [draftReturn, orderId, warrantyId, returnType, productIds]);

  // Grab the RPC client and subscribe to errors
  const client = useRequiredContext(RpcClientContext);
  const rpcClientErrorSubscribers = useContext(
    RpcClientErrorSubscribersContext,
  );
  useEffect(() => {
    const cancelErrorSubscriptionToken = Symbol();
    rpcClientErrorSubscribers.set(
      cancelErrorSubscriptionToken,
      async (rpcName: keyof CustomerPortalRpcClient, error: unknown) => {
        if (error instanceof Response) {
          if (
            error.status === 404 &&
            (await error.text()).includes("Draft return not found")
          ) {
            setExpired(true);
            clearDraftReturnID(draftReturnIDParams);
          }
        }
      },
    );
    // Returns a cleanup function to remove the error subscriber on component unmount
    return () => {
      rpcClientErrorSubscribers.delete(cancelErrorSubscriptionToken);
    };
  });

  // Provide a way to reset the session if the user double clicks with multiple modifier keys
  useWindowEventListener(window, "dblclick", (e) => {
    if (
      [e.ctrlKey, e.shiftKey, e.altKey, e.metaKey].filter(Boolean).length >= 2
    ) {
      setExpired(true);
      clearDraftReturnID(draftReturnIDParams);
    }
  });

  const initialLoad = useLoad(
    async (signal) => {
      let draftReturn: DraftReturn | null = null;
      let requestDetails:
        | {
            request: string;
            draftReturnId?: string;
            orderId?: string;
            returnType?: ReturnTypeEnum;
            email?: string;
          }
        | {} = {};
      try {
        const draftReturnId =
          passedDraftReturnId ?? getDraftReturnID(draftReturnIDParams);
        if (draftReturnId) {
          requestDetails = { request: "getDraftReturn", draftReturnId };
          const getDraftReturnResult = await client.getDraftReturn(
            { draftReturnId },
            { signal },
          );
          draftReturn = getDraftReturnResult.draftReturn;
          if (draftReturn) {
            setDraftReturn(draftReturn);
            return;
          }
        }
        // If we don't have a cached draft returnID, or the draft return is not found, create a new one.
        if (productIds || !orderId) {
          requestDetails = {
            request: "createOrderlessDraftReturn",
            productIds: productIds || [],
            returnType,
            isThirdPartyClaim,
          };
          const createOrderlessDraftReturnResult =
            await client.createOrderlessDraftReturn(
              {
                productIds: productIds || [],
                warrantyId,
                returnType,
                isThirdPartyClaim,
              },
              { signal },
            );
          draftReturn = createOrderlessDraftReturnResult.draftReturn;
        } else {
          requestDetails = {
            request: "createDraftReturn",
            orderId,
            returnType: returnType as ReturnTypeEnum,
            email: user?.email,
          };
          let address;
          if (user?.address) {
            const parsedAddress = JSON.parse(user.address);
            if (parsedAddress) {
              address = {
                street1:
                  (parsedAddress.street1 || parsedAddress.address1) ?? "",
                street2:
                  (parsedAddress.street2 || parsedAddress.address2) ?? "",
                city: parsedAddress.city ?? "",
                state: parsedAddress.state ?? "",
                zip: parsedAddress.zip ?? "",
                country: parsedAddress.country ?? "US",
                phone: parsedAddress.phone ?? "",
                name: (user?.guestName || user?.name) ?? "",
                email: (user?.guestEmail || user?.email) ?? "",
              };
            }
          }
          const referralId = referralContext?.referralId || null;

          const createDraftReturnResult = await client.createDraftReturn(
            {
              orderId,
              returnType: returnType as ReturnTypeEnum,
              email: user?.email,
              address,
              customerTimezone: Temporal.Now.timeZoneId(),
              referralId,
            },
            { signal },
          );

          if (referralId && referralContext) {
            referralContext.clearReferralId();
          }

          draftReturn = createDraftReturnResult.draftReturn;
        }
        setDraftReturnID(draftReturnIDParams, draftReturn._id);
        setDraftReturn(draftReturn);
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          // Handle the abort error specifically
          console.warn(
            `Request was aborted: ${JSON.stringify(requestDetails)}`,
          );
        } else {
          throw error; // Re-throw other errors
        }
      } finally {
        const params = new URLSearchParams(window.location.search);
        if (
          draftReturn?.selectedMethod === CompensationMethodType.EXCHANGE &&
          settings.exchanges.shopSiteOneTab &&
          params.has("exchange-complete")
        ) {
          setStep(ReturnStep.NewItems);
        } else if (!passedDraftReturnId) {
          // if there are new order items, we want to remove them so that only variant exchange items with price difference "apply" are left in new order data (enso's engraving fee should also survive this removal)
          if (
            draftReturn?._id &&
            (draftReturn?.newOrderData?.items || []).length > 0
          ) {
            const removedItemDraftReturn = await client.removeNewItems({
              draftReturnId: draftReturn._id,
              variantIds: (draftReturn?.newOrderData?.items || []).map(
                (item) => item.variantId,
              ),
            });
            setDraftReturn(removedItemDraftReturn.draftReturn);
          }
        }
      }
    },
    [orderId],
  );

  const friendlyErrorMessageText = useMemo(() => {
    if (!initialLoad.error) {
      return undefined;
    }
    return friendlyErrorMessage(
      initialLoad.error,
      friendlyCreateDraftReturnErrorMessages,
    );
  }, [initialLoad.error]);

  const errorMessageHref = useMemo(() => {
    const subject = `Return portal load error on ${settings.merchantName}: ${orderId}`;
    const body = `I encountered an error while loading the return portal${friendlyErrorMessageText ? `: ${friendlyErrorMessageText}` : ""}`;
    return `mailto:support@getredo.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [orderId, settings.merchantName, initialLoad.error]);

  const [_load, setDefaultMethodAndProgressToShipmentMethod] = useTriggerLoad(
    async (signal) => {
      if (!draftReturn) {
        return;
      }

      // Filter out same item/variant exchanges as they were taken care of earlier
      const availableMethods = availableMethodsForCompensationMethodsModal(
        draftReturn.compensationMethods,
      );

      if (availableMethods.length !== 1) {
        return;
      }
      const { draftReturn: updatedDraftReturn } =
        await client.setCompensationMethod(
          {
            draftReturnId: draftReturn._id,
            selectedMethod: availableMethods[0].type,
          },
          { signal },
        );

      setDraftReturn(updatedDraftReturn);
      // TODO, if it's a green return method, don't bother going to shipping method
      setStep(ReturnStep.ShipmentMethod);
    },
  );

  const onConfirmSelectedProduct = useHandler(
    async (selectedProduct: ReturnProduct) => {
      if (!draftReturn) {
        return;
      }

      const { draftReturn: updatedDraftReturn } =
        await client.addProductToOrderlessDraftReturn({
          draftReturnId: draftReturn._id,
          productIds: [selectedProduct.productId],
        });

      setDraftReturn(updatedDraftReturn);
      setStep(ReturnStep.Items);
    },
  );

  const onChooseItemsComplete = useHandler((draftReturn: DraftReturn) => {
    if (!draftReturn) {
      return;
    }

    const optedInToExtendedWarranty = draftReturn.pendingReturnItems.some(
      (item) =>
        item.inputAnswers?.some(
          (answer) => answer.fieldId === "warranties/path/ew-upsell",
        ),
    );
    const reviewWarrantyRegistration = isRegister && optedInToExtendedWarranty;
    if (reviewWarrantyRegistration) {
      setStep(ReturnStep.RegistrationReview);
      return;
    }

    // Filter out same item/variant exchanges as they were taken care of earlier
    const availableMethods = availableMethodsForCompensationMethodsModal(
      draftReturn.compensationMethods,
    );

    if (availableMethods.length) {
      if (
        availableMethods.length > 1 ||
        (availableMethods.length === 1 &&
          availableMethods[0].type === CompensationMethodType.EXCHANGE &&
          availableMethods[0].advancedExchangeAvailable)
      ) {
        setStep(ReturnStep.CompensationMethod);
      } else {
        setDefaultMethodAndProgressToShipmentMethod();
      }
    } else {
      // This should not happen
      console.error("No compensation methods found");
    }
  });

  const [exchangeContext, setExchangeContext] = useState<ExchangeContext>({});
  const onCompensationMethodComplete = useHandler(
    async (method: CompensationMethod, exchangeContext: ExchangeContext) => {
      if (method.type === CompensationMethodType.EXCHANGE) {
        setExchangeContext(exchangeContext);
        setStep(ReturnStep.NewItems);
      } else if (Object.values(method.shippingRequired).some(Boolean)) {
        setStep(ReturnStep.ShipmentMethod);
      } else {
        setStep(ReturnStep.Review);
      }
    },
  );

  const onShipmentMethodComplete = useHandler(
    async (method?: ShipmentMethod) => {
      setStep(ReturnStep.Review);
    },
  );

  const onNewItemsComplete = useHandler(() => {
    if (!draftReturn) {
      return;
    }
    const method = draftReturn.compensationMethods.find(
      (method) => method.type === draftReturn.selectedMethod,
    );
    if (!method) {
      // If we somehow end up without a selected compensation method, go back to the beginning
      setStep(ReturnStep.Items);
    } else if (Object.values(method.shippingRequired).some(Boolean)) {
      setStep(ReturnStep.ShipmentMethod);
    } else {
      setStep(ReturnStep.Review);
    }
  });

  if (initialLoad.pending) {
    return <LoadingRedoAnimation />;
  }

  if (step === ReturnStep.ProductSelect) {
    return (
      <ProductSelect
        handleContinue={onConfirmSelectedProduct}
        settings={settings}
      />
    );
  }

  return draftReturn && draftReturn?.returnableItems?.length > 0 ? (
    <>
      {step === ReturnStep.Items ? (
        <ChooseItems
          draftReturn={draftReturn}
          isRegistrationClaim={warrantyId !== undefined}
          notes={notes}
          onComplete={onChooseItemsComplete}
          onReject={warrantyId ? onBack : undefined}
          registerWarranty={isRegister}
          returnType={returnType}
          setDraftReturn={setDraftReturn}
          settings={settings}
        />
      ) : step === ReturnStep.CompensationMethod ? (
        <>
          <ChooseItems
            draftReturn={draftReturn}
            onComplete={() => {}}
            registerWarranty={isRegister}
            returnType={returnType}
            setDraftReturn={setDraftReturn}
            settings={settings}
          />
          <CompensationMethodModal
            draftReturn={draftReturn}
            onCancel={() => setStep(ReturnStep.Items)}
            onComplete={onCompensationMethodComplete}
            setDraftReturn={setDraftReturn}
            settings={settings}
          />
        </>
      ) : step === ReturnStep.NewItems ? (
        <NewItems
          ctx={exchangeContext}
          draftReturn={draftReturn}
          onComplete={onNewItemsComplete}
          setDraftReturn={setDraftReturn}
          settings={settings}
        />
      ) : step === ReturnStep.ShipmentMethod ? (
        <ShipmentMethodsModal
          draftReturn={draftReturn}
          onCancel={() => setStep(ReturnStep.Items)}
          onComplete={onShipmentMethodComplete}
          setDraftReturn={setDraftReturn}
          settings={settings}
        />
      ) : step === ReturnStep.Review ? (
        <div>
          <Review
            callback={callback}
            client={client}
            initialDraftReturn={draftReturn}
            settings={settings}
            user={user}
          />
        </div>
      ) : step === ReturnStep.RegistrationReview ? (
        <div>
          <RegistrationReview
            client={client}
            draftReturn={draftReturn}
            setDraftReturn={setDraftReturn}
            settings={settings}
            user={user}
          />
        </div>
      ) : (
        (() => {
          throw new Error(`Invalid return step: ${step}`);
        })()
      )}
      <Modal
        footer={
          <Button
            onClick={() => window.location.reload()}
            theme={ButtonTheme.PRIMARY}
          >
            Reload
          </Button>
        }
        hideCloseButton
        open={expired}
        title="Session expired"
      >
        <div>
          Return session expired. Please reload the page to start again.
        </div>
      </Modal>
    </>
  ) : initialLoad?.error ? (
    <ContactRedoModal
      errorMessage={friendlyErrorMessageText}
      errorMessageHref={errorMessageHref}
      title="Return portal error"
    />
  ) : draftReturn?.returnableItems?.length === 0 ? (
    <div className={returnStyles.mainHeader}>
      You have no items eligible for return
    </div>
  ) : null;
}
